import React, { useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, AppBar, Toolbar, IconButton, Typography, Drawer, List,
  ListItemButton, ListItemIcon, ListItemText, Divider, Avatar,
  useMediaQuery, useTheme, Chip, Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon,
  Inventory as InventoryIcon, PointOfSale as PosIcon,
  Receipt as ReceiptIcon, AccountBalanceWallet as CashIcon,
  SwapHoriz as TransferIcon, Assessment as ReportIcon,
  Discount as DiscountIcon, Store as StoreIcon,
} from '@mui/icons-material';

import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import PointOfSale from './pages/PointOfSale';
import Sales from './pages/Sales';
import CashRegister from './pages/CashRegister';
import Transfers from './pages/Transfers';
import StoresPage from './pages/Stores';
import PromotionsPage from './pages/Promotions';

import { sucursalActual, nombreTienda } from './data';

const DRAWER = 240;

const NAV = [
  { texto: 'Dashboard', ruta: '/dashboard', icono: <DashboardIcon /> },
  { texto: 'Productos', ruta: '/productos', icono: <InventoryIcon /> },
  { texto: 'Punto de Venta', ruta: '/pos', icono: <PosIcon /> },
  { texto: 'Ventas', ruta: '/ventas', icono: <ReceiptIcon /> },
  { texto: 'Corte de Caja', ruta: '/cortes', icono: <CashIcon /> },
  { texto: 'Transferencias', ruta: '/transferencias', icono: <TransferIcon /> },
  { texto: 'Sucursales', ruta: '/sucursales', icono: <StoreIcon /> },
  { texto: 'Promociones', ruta: '/promociones', icono: <DiscountIcon /> },
];

const TITULOS = {
  '/dashboard': 'Dashboard', '/productos': 'Productos', '/pos': 'Punto de Venta',
  '/ventas': 'Ventas', '/cortes': 'Corte de Caja', '/transferencias': 'Transferencias',
  '/sucursales': 'Sucursales', '/promociones': 'Promociones',
};

function App() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const esDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const path = location.pathname;
  const titulo = TITULOS[path] || 'WHS Demo';

  const esActivo = (ruta) => {
    if (ruta === '/dashboard') return path === '/dashboard';
    return path.startsWith(ruta);
  };

  const handleNav = (ruta) => {
    navigate(ruta);
    if (!esDesktop) setMobileOpen(false);
  };

  const contenido = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2, gap: 1.5, minHeight: '64px !important' }}>
        <Avatar sx={{ bgcolor: sucursalActual.color_primario, width: 36, height: 36, fontSize: '0.95rem', fontWeight: 800 }}>
          WHS
        </Avatar>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            WHS Demo
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sucursalActual.nombre}
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, px: 1, py: 1 }}>
        {NAV.map((item) => (
          <ListItemButton
            key={item.ruta}
            selected={esActivo(item.ruta)}
            onClick={() => handleNav(item.ruta)}
            sx={{
              borderRadius: 2, mb: 0.5, minHeight: 44,
              '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '& .MuiListItemIcon-root': { color: '#fff' } },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icono}</ListItemIcon>
            <ListItemText primary={item.texto} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: esActivo(item.ruta) ? 700 : 500 }} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Demo — Solo lectura
        </Typography>
        <Chip label={nombreTienda(sucursalActual.codigo)} size="small" sx={{ mt: 0.5, bgcolor: sucursalActual.color_primario, color: '#fff' }} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER } }}>
        {contenido}
      </Drawer>
      <Drawer variant="permanent" open
        sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: DRAWER } }}>
        {contenido}
      </Drawer>

      <Box sx={{ flexGrow: 1, ml: { md: `${DRAWER}px` }, display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <AppBar position="sticky" sx={{ bgcolor: sucursalActual.color_primario }}>
          <Toolbar sx={{ gap: 1 }}>
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)}
              sx={{ display: { md: 'none' } }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700, fontSize: { xs: '1rem', md: '1.2rem' } }}>
              {titulo}
            </Typography>
            <Chip label="DEMO" color="warning" size="small" />
          </Toolbar>
        </AppBar>

        <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 }, overflow: 'auto' }}>
          <Routes>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="productos" element={<Products />} />
            <Route path="pos" element={<PointOfSale />} />
            <Route path="ventas" element={<Sales />} />
            <Route path="cortes" element={<CashRegister />} />
            <Route path="transferencias" element={<Transfers />} />
            <Route path="sucursales" element={<StoresPage />} />
            <Route path="promociones" element={<PromotionsPage />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}

export default App;
